import { useRef, useState } from "react";
import { domToBlob } from "modern-screenshot";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Camera, X, Upload } from "lucide-react";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5MB
const SCREENSHOT_BUCKET = "feedback-screenshots";

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setScreenshot = (file: File) => {
    if (file.size > MAX_SCREENSHOT_BYTES) {
      toast.error("Screenshot too large", {
        description: "Please attach an image under 5MB.",
      });
      return;
    }
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotFile(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const captureScreenshot = async () => {
    setIsCapturing(true);

    try {
      const blob = await domToBlob(document.documentElement, {
        type: "image/png",
        quality: 0.92,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        backgroundColor: getComputedStyle(document.body).backgroundColor || "#000",
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          // Skip the feedback dialog content itself
          if (node.getAttribute("role") === "dialog") return false;
          // Skip Radix popper-rendered floating UI
          if (node.hasAttribute("data-radix-popper-content-wrapper")) return false;
          // Skip Sonner toaster portals
          if (node.hasAttribute("data-sonner-toaster")) return false;
          // Skip the Radix Dialog overlay (fixed inset-0 with bg-black/80)
          const cls = typeof node.className === "string" ? node.className : "";
          if (cls.includes("bg-black/80") && cls.includes("fixed")) return false;
          return true;
        },
      });

      if (!blob) throw new Error("Failed to capture screenshot");

      const file = new File([blob], `screenshot-${Date.now()}.png`, { type: "image/png" });
      setScreenshot(file);
    } catch (err: any) {
      console.error("Screenshot capture failed:", err);
      toast.error("Couldn't capture screenshot", {
        description: "Try uploading an image file instead.",
      });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setScreenshot(file);
    e.target.value = "";
  };

  const clearScreenshot = () => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotFile(null);
    setScreenshotPreview(null);
  };

  const resetForm = () => {
    setSubject("");
    setMessage("");
    clearScreenshot();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast.error("You must be logged in to send feedback");
        setIsSubmitting(false);
        return;
      }

      let screenshotPath: string | null = null;
      if (screenshotFile) {
        const ext = screenshotFile.type === "image/jpeg" ? "jpg" : "png";
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from(SCREENSHOT_BUCKET)
          .upload(path, screenshotFile, {
            cacheControl: "3600",
            upsert: false,
            contentType: screenshotFile.type || "image/png",
          });
        if (uploadError) throw uploadError;
        screenshotPath = path;
      }

      const { error } = await supabase.functions.invoke("send-feedback", {
        body: {
          subject,
          message,
          screenshotPath,
        },
      });

      if (error) throw error;

      toast.success("Thank you for your feedback!");

      resetForm();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error sending feedback:", error);
      const errorMessage = error?.message || error?.error || "Failed to send feedback. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent
        hideCloseButton
        className="w-[min(24rem,calc(100%-2.5rem))] sm:max-w-[500px] max-h-[82dvh] !flex flex-col overflow-visible rounded-3xl pt-6 bg-card/90 backdrop-blur-2xl shadow-2xl shadow-black/50 border-foreground/10"
      >
        {/* Close tile floats above the card, like the create-event window */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          className="absolute -top-11 right-0 inline-flex h-9 w-9 items-center justify-center rounded-xl border-0 bg-card/70 text-foreground backdrop-blur-xl shadow-[0_10px_15px_-3px_rgba(0,0,0,0.45)] transition-all duration-300 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>
            Help us improve Nightspy by sharing your thoughts, reporting issues, or suggesting features.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto no-scrollbar -mx-6 px-6 pb-1 flex-1">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              placeholder="Brief description of your feedback"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Details</Label>
            <Textarea
              id="message"
              placeholder="Please provide details about your feedback, issue, or suggestion..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting}
              rows={5}
            />
          </div>

          <div className="space-y-2">
            <Label>Screenshot (optional)</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleFilePicked}
            />
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full px-2"
                onClick={captureScreenshot}
                disabled={isSubmitting || isCapturing}
              >
                {isCapturing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="mr-2 h-4 w-4" />
                )}
                {screenshotPreview ? "Retake" : "Add Screenshot"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full px-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting || isCapturing}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload Image
              </Button>
            </div>
            {screenshotPreview ? (
              <div className="relative w-full rounded-md border border-border overflow-hidden bg-muted/40 mt-2">
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="block w-full"
                  aria-label="View full screenshot"
                >
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="w-full max-h-48 object-contain cursor-zoom-in"
                  />
                </button>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={clearScreenshot}
                  disabled={isSubmitting}
                  aria-label="Remove screenshot"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                We'll capture the page behind this dialog at your current screen size.
              </p>
            )}
          </div>

          <div className="flex justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Feedback
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogContent className="max-w-[95vw] sm:max-w-4xl p-2 bg-background">
        <DialogHeader className="sr-only">
          <DialogTitle>Screenshot preview</DialogTitle>
        </DialogHeader>
        {screenshotPreview && (
          <img
            src={screenshotPreview}
            alt="Screenshot full preview"
            className="w-full max-h-[85vh] object-contain rounded-md"
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}